import { useEffect, useRef } from "react";
import dayjs, { type Dayjs } from "dayjs";
import {
  type FlightCoreRawEvent,
  useEmitCoreFlightRawEvent,
} from "../../../tracking";

export type RawEventWithRuntime = Extract<
  FlightCoreRawEvent,
  { properties: { runtime: number } }
>;

export type RawEventWithRuntimeNotProvided = {
  name: RawEventWithRuntime["name"];
  properties: Omit<RawEventWithRuntime["properties"], "runtime">;
};
/** This hook is used to track the runtime of an event.
 * It will append the runtime in seconds
 *
 * To create a raw event which can be tracked using this function it should have
 * a properties object with a runtime key of type number
 *
 * ExampleRawEvent = {
 *   name: ExampleRawEventName.ExampleRawEvent,
 *   properties: {
 *     property1: string,
 *     runtime: number,
 *   }
 * }
 *
 * As a difference with useEmitCoreFlightRawEventOnce
 * if the event only tracks the runtime and does not have any other properties
 *
 * ExampleRawEvent = {
 *   name: ExampleRawEventName.ExampleRawEvent,
 *   properties: {
 *     runtime: number,
 *   }
 * }
 *
 * To track the event you should call this hook with an empty object as properties
 * to pass the type check
 *
 * useTrackEventRuntime({ name: ExampleRawEventName.ExampleRawEvent, properties: {} }, condition)
 * */

export const useTrackEventRuntime = (
  event: RawEventWithRuntimeNotProvided,
  condition: boolean
) => {
  const emitRawWithRuntimeNotProvidedEvent = useEmitCoreFlightRawEvent(
    event.name
  );

  const startLoadingTime = useRef<Dayjs>();
  useEffect(() => {
    if (condition && !startLoadingTime.current) {
      startLoadingTime.current = dayjs();
    }
    if (startLoadingTime.current && !condition) {
      const runtime = dayjs().diff(startLoadingTime.current, "seconds", true);

      const eventProperties =
        "properties" in event && event.properties != null
          ? event.properties
          : {};

      emitRawWithRuntimeNotProvidedEvent({
        ...event,
        properties: {
          ...eventProperties,
          runtime,
        },
      });

      startLoadingTime.current = undefined;
    }
  }, [condition, event, emitRawWithRuntimeNotProvidedEvent]);
};
